import Ionicons from "@expo/vector-icons/Ionicons";
import { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import {
  clampPage,
  PAGE_GAP,
  PAGE_SIZE,
  pageCount,
  pageSlice,
  pageWindow,
} from "@/lib/paging";

/**
 * Hold one list's current page.
 *
 * The page is clamped on read rather than corrected in an effect, so a list
 * that shrinks underneath the reader (a goal deleted, a filter applied) simply
 * lands on the new last page instead of rendering blank and then jumping.
 */
export function usePagedItems<T>(items: T[], pageSize = PAGE_SIZE) {
  const [requested, setRequested] = useState(1);
  const totalPages = pageCount(items.length, pageSize);
  const page = clampPage(requested, totalPages);
  return {
    page,
    totalPages,
    visible: pageSlice(items, page, pageSize),
    setPage: setRequested,
  };
}

/**
 * Numbered pager: ‹ 1 … 4 5 6 … 9 ›.
 *
 * Renders nothing for a single page — a pager under five rows is noise. When
 * `hasMore` is set the list is still being paged in from the server, so the
 * next arrow stays enabled past the last known page and `onNextBeyondEnd` is
 * asked to fetch.
 */
export function Pager({
  page,
  totalPages,
  onChange,
  hasMore = false,
  busy = false,
  onNextBeyondEnd,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  hasMore?: boolean;
  busy?: boolean;
  onNextBeyondEnd?: () => void;
}) {
  const c = useTheme();
  if (totalPages <= 1 && !hasMore) return null;

  const slots = pageWindow(page, totalPages, hasMore);
  const atStart = page <= 1;
  const atEnd = page >= totalPages;
  // `hasMore` without a fetcher means "more exist that this screen will never
  // load" — a capped server-side evidence window. The trailing "…" still says
  // so, but the arrow must not invite a tap that does nothing.
  const canGoNext = (!atEnd || (hasMore && !!onNextBeyondEnd)) && !busy;

  function goNext() {
    if (!atEnd) {
      onChange(page + 1);
      return;
    }
    // Last loaded page with more behind it: ask the owner to fetch, and it
    // moves the page on once the rows arrive.
    onNextBeyondEnd?.();
  }

  return (
    <View style={styles.row}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: atStart }}
        accessibilityLabel={i18n.t("growth.dashboard.prevPageA11y")}
        disabled={atStart}
        onPress={() => onChange(page - 1)}
        hitSlop={6}
        style={[
          styles.arrow,
          { borderColor: c.border },
          atStart && styles.disabled,
        ]}
      >
        <Ionicons name="chevron-back" size={16} color={c.textSecondary} />
      </Pressable>

      {slots.map((slot, index) =>
        slot === PAGE_GAP ? (
          <ThemedText
            key={`gap-${index}`}
            variant="caption"
            tone="muted"
            accessibilityElementsHidden
            importantForAccessibility="no"
            style={styles.gap}
          >
            …
          </ThemedText>
        ) : (
          <Pressable
            key={slot}
            accessibilityRole="button"
            accessibilityState={{ selected: slot === page }}
            accessibilityLabel={i18n.t("growth.dashboard.pageA11y", {
              page: slot,
            })}
            onPress={() => onChange(slot)}
            hitSlop={4}
            style={[
              styles.page,
              {
                borderColor: slot === page ? c.tint : c.border,
                backgroundColor:
                  slot === page ? c.backgroundSelected : "transparent",
              },
            ]}
          >
            <ThemedText
              variant="caption"
              tone={slot === page ? "tint" : "secondary"}
              maxFontSizeMultiplier={1.2}
              style={slot === page ? styles.currentLabel : undefined}
            >
              {slot}
            </ThemedText>
          </Pressable>
        ),
      )}

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !canGoNext }}
        accessibilityLabel={i18n.t("growth.dashboard.nextPageA11y")}
        disabled={!canGoNext}
        onPress={goNext}
        hitSlop={6}
        style={[
          styles.arrow,
          { borderColor: c.border },
          !canGoNext && styles.disabled,
        ]}
      >
        <Ionicons name="chevron-forward" size={16} color={c.textSecondary} />
      </Pressable>
    </View>
  );
}

const HIT = 32;

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.xs,
    paddingTop: Spacing.sm,
  },
  page: {
    minWidth: HIT,
    height: HIT,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 6,
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  currentLabel: { fontWeight: "800" },
  arrow: {
    width: HIT,
    height: HIT,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  disabled: { opacity: 0.4 },
  gap: { paddingHorizontal: 2 },
});
